import { Navigate, useParams } from 'react-router-dom';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { useLogoutMutation, useSessionQuery } from '@/api/login';
import { useInvitationAcceptDetailsQuery } from '@/api/user';
import { loginFor } from '@/services/emailed-link';
import { InvitationMessage } from './invitation-message';

// Home for a signed-in visitor: the root route routes a session through
// terms, orphan and launch.
const onHome = () => {
  window.location.assign('/');
};

// The emailed invitation link lands here. The link's job is to get the
// recipient to log in or create an account; accepting happens in the
// pending list.
//
// Without a session the visitor goes to the log-in page for the link,
// where create-an-account is beside it. With a session, one authenticated
// read on the token answers yours-or-not on the live rule: yours, and the
// invitation is waiting in the pending list; not yours, and they are told
// so. Nothing is accepted or declined here.
const InvitationAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const loggedIn = !!session?.accessToken;
  const {
    data: details,
    error: detailsError,
    isLoading: isDetailsLoading,
  } = useInvitationAcceptDetailsQuery(token, { skip: !loggedIn });
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();

  if (isSessionLoading || (loggedIn && isDetailsLoading)) {
    return <Loading />;
  }

  if (!loggedIn) {
    return <Navigate to={loginFor({ kind: 'invitation', token })} replace />;
  }

  if (details?.state === 'pending') {
    return <Navigate to='/invitations' replace />;
  }

  const status = (detailsError as any)?.status;
  const code = (detailsError as any)?.data?.code;
  const notFound = status === 404;
  const notForThisAccount = code === 'invitation-not-for-this-account';

  return (
    <Center>
      {details?.state === 'spent' && (
        <InvitationMessage
          title='Already accepted'
          actions={[{ label: 'Go to Kinesis', onClick: onHome }]}
        >
          This invitation has already been accepted.
        </InvitationMessage>
      )}
      {details?.state === 'expired' && (
        <InvitationMessage title='Invitation expired'>
          This invitation has expired. Ask whoever invited you to send it again.
        </InvitationMessage>
      )}
      {notForThisAccount && (
        <InvitationMessage
          title='Not for this account'
          actions={[
            {
              label: 'Log in with another account',
              onClick: () =>
                logout({ to: loginFor({ kind: 'invitation', token }) }),
              loading: isLoggingOut,
            },
            { label: 'Go to Kinesis', onClick: onHome, variant: 'secondary' },
          ]}
        >
          This invitation was sent to a different email address. You’re signed
          in as <strong>{session?.email}</strong>. Ask whoever invited you to
          send it to this address, or log in with the address it was sent to.
        </InvitationMessage>
      )}
      {notFound && (
        <InvitationMessage title='Invitation not found'>
          This invitation isn’t available. Ask whoever invited you to send it
          again.
        </InvitationMessage>
      )}
      {!details && detailsError && !notFound && !notForThisAccount && (
        <InvitationMessage title='Something went wrong'>
          We couldn’t open this invitation. Please try again.
        </InvitationMessage>
      )}
    </Center>
  );
};

export { InvitationAccept };
